/**
 * Import parsers for common service-locator export formats.
 * Supported: KML, KMZ, GeoJSON, GPX, CSV.
 *
 * All parsers normalise to `{ pins, lines, bounds }` in the same shape as the
 * job model so they can be spread into `job.pins` / `job.lines` directly.
 */
import { kml } from "@tmcw/togeojson";
import JSZip from "jszip";

// ---------------------------------------------------------------------------
// Format detection
// ---------------------------------------------------------------------------
export function detectSurveyKind(file) {
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  if (name.endsWith(".kml") || type.includes("kml")) return "kml";
  if (name.endsWith(".kmz") || type.includes("kmz")) return "kmz";
  if (name.endsWith(".geojson") || name.endsWith(".json") || type.includes("geo+json")) return "geojson";
  if (name.endsWith(".gpx") || type.includes("gpx")) return "gpx";
  if (name.endsWith(".csv") || type.includes("csv")) return "csv";
  return null;
}

// ---------------------------------------------------------------------------
// Bounds tracker
// ---------------------------------------------------------------------------
function makeBoundsTracker() {
  let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity;
  return {
    track(lat, lng) {
      if (typeof lat !== "number" || typeof lng !== "number" || isNaN(lat) || isNaN(lng)) return;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    },
    get() {
      return isFinite(minLat) ? [[minLat, minLng], [maxLat, maxLng]] : null;
    },
  };
}

// ---------------------------------------------------------------------------
// GeoJSON -> pins/lines (also target for KML/KMZ after conversion)
// ---------------------------------------------------------------------------
export function geojsonToPinsAndLines(geojson, service) {
  const pins = [];
  const lines = [];
  const bt = makeBoundsTracker();
  const features = geojson?.features || [];

  for (const feat of features) {
    const name = feat?.properties?.name || feat?.properties?.Name || "";
    const geom = feat?.geometry;
    if (!geom) continue;

    if (geom.type === "Point") {
      const [lng, lat] = geom.coordinates;
      if (typeof lat === "number" && typeof lng === "number") {
        pins.push({ id: crypto.randomUUID(), service, lat, lng, label: name, notes: "", depth_cm: null });
        bt.track(lat, lng);
      }
    } else if (geom.type === "MultiPoint") {
      for (const [lng, lat] of geom.coordinates) {
        if (typeof lat === "number" && typeof lng === "number") {
          pins.push({ id: crypto.randomUUID(), service, lat, lng, label: name, notes: "", depth_mm: null });
          bt.track(lat, lng);
        }
      }
    } else if (geom.type === "LineString") {
      const coords = geom.coordinates
        .filter((c) => c && typeof c[0] === "number" && typeof c[1] === "number")
        .map(([lng, lat]) => { bt.track(lat, lng); return [lat, lng]; });
      if (coords.length >= 2) lines.push({ id: crypto.randomUUID(), service, coords, label: name });
    } else if (geom.type === "MultiLineString") {
      for (const seg of geom.coordinates) {
        const coords = seg
          .filter((c) => c && typeof c[0] === "number" && typeof c[1] === "number")
          .map(([lng, lat]) => { bt.track(lat, lng); return [lat, lng]; });
        if (coords.length >= 2) lines.push({ id: crypto.randomUUID(), service, coords, label: name });
      }
    } else if (geom.type === "Polygon") {
      for (const ring of geom.coordinates) {
        const coords = ring
          .filter((c) => c && typeof c[0] === "number" && typeof c[1] === "number")
          .map(([lng, lat]) => { bt.track(lat, lng); return [lat, lng]; });
        if (coords.length >= 2) lines.push({ id: crypto.randomUUID(), service, coords, label: name });
      }
    }
  }
  return { pins, lines, bounds: bt.get() };
}

// ---------------------------------------------------------------------------
// KML / KMZ
// ---------------------------------------------------------------------------
async function parseKml(file) {
  const text = await file.text();
  const doc = new DOMParser().parseFromString(text, "application/xml");
  return kml(doc);
}
async function parseKmz(file) {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const entry = Object.values(zip.files).find((f) => /\.kml$/i.test(f.name) && !f.dir);
  if (!entry) throw new Error("No .kml file inside KMZ");
  const text = await entry.async("string");
  const doc = new DOMParser().parseFromString(text, "application/xml");
  return kml(doc);
}

// ---------------------------------------------------------------------------
// GPX (waypoints + track points)
// ---------------------------------------------------------------------------
function parseGpxToGeoJson(xmlText) {
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  const features = [];

  // Waypoints
  doc.querySelectorAll("wpt").forEach((w) => {
    const lat = parseFloat(w.getAttribute("lat"));
    const lon = parseFloat(w.getAttribute("lon"));
    const name = w.querySelector("name")?.textContent?.trim() || "";
    if (!isNaN(lat) && !isNaN(lon)) {
      features.push({
        type: "Feature",
        properties: { name },
        geometry: { type: "Point", coordinates: [lon, lat] },
      });
    }
  });

  // Tracks — each segment becomes a LineString
  doc.querySelectorAll("trk").forEach((trk) => {
    const trkName = trk.querySelector("name")?.textContent?.trim() || "";
    trk.querySelectorAll("trkseg").forEach((seg) => {
      const coords = [];
      seg.querySelectorAll("trkpt").forEach((pt) => {
        const lat = parseFloat(pt.getAttribute("lat"));
        const lon = parseFloat(pt.getAttribute("lon"));
        if (!isNaN(lat) && !isNaN(lon)) coords.push([lon, lat]);
      });
      if (coords.length >= 2) {
        features.push({
          type: "Feature",
          properties: { name: trkName },
          geometry: { type: "LineString", coordinates: coords },
        });
      }
    });
  });

  // Routes — LineStrings from <rte><rtept>
  doc.querySelectorAll("rte").forEach((rte) => {
    const name = rte.querySelector("name")?.textContent?.trim() || "";
    const coords = [];
    rte.querySelectorAll("rtept").forEach((pt) => {
      const lat = parseFloat(pt.getAttribute("lat"));
      const lon = parseFloat(pt.getAttribute("lon"));
      if (!isNaN(lat) && !isNaN(lon)) coords.push([lon, lat]);
    });
    if (coords.length >= 2) {
      features.push({
        type: "Feature",
        properties: { name },
        geometry: { type: "LineString", coordinates: coords },
      });
    }
  });

  return { type: "FeatureCollection", features };
}

// ---------------------------------------------------------------------------
// CSV — headers auto-detected (lat/latitude/y, lng/lon/longitude/x, name/label,
//                                depth/depth_cm)
// ---------------------------------------------------------------------------
function splitCsvLine(line) {
  // Minimal RFC-4180-ish split (handles simple quoted values).
  const out = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') { q = false; }
      else cur += c;
    } else {
      if (c === ",") { out.push(cur); cur = ""; }
      else if (c === '"') { q = true; }
      else cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseCsvToPinsAndLines(text, service) {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { pins: [], lines: [], bounds: null };

  const headers = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
  const idx = (patterns) => headers.findIndex((h) => patterns.some((p) => h === p || h.includes(p)));
  const latIdx = idx(["latitude", "lat", "y"]);
  const lngIdx = idx(["longitude", "lon", "lng", "x"]);
  const nameIdx = idx(["name", "label", "description", "id"]);
  const depthIdx = idx(["depth_mm", "depth_cm", "depth", "dep"]);

  if (latIdx < 0 || lngIdx < 0) {
    throw new Error("CSV must include Latitude and Longitude columns");
  }

  const pins = [];
  const bt = makeBoundsTracker();
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i]);
    const lat = parseFloat(cells[latIdx]);
    const lng = parseFloat(cells[lngIdx]);
    if (isNaN(lat) || isNaN(lng)) continue;
    const label = nameIdx >= 0 ? (cells[nameIdx] || "") : "";
    const depth_raw = depthIdx >= 0 ? cells[depthIdx] : "";
    let depth_mm = depth_raw && !isNaN(Number(depth_raw)) ? Number(depth_raw) : null;
    // Legacy CSVs that use a "depth_cm" header — convert to mm.
    if (depth_mm != null && depthIdx >= 0 && headers[depthIdx].includes("cm")) depth_mm *= 10;
    pins.push({ id: crypto.randomUUID(), service, lat, lng, label, notes: "", depth_mm });
    bt.track(lat, lng);
  }

  return { pins, lines: [], bounds: bt.get() };
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------
/**
 * Parse any supported survey file and return normalised pins/lines/bounds.
 * @param {File} file
 * @param {string} service - active utility key (used to colour imports)
 * @returns {Promise<{ pins: Array, lines: Array, bounds: any, kind: string }>}
 */
export async function importSurveyFile(file, service) {
  const kind = detectSurveyKind(file);
  if (!kind) throw new Error("Unsupported file type");

  if (kind === "kml") {
    const gj = await parseKml(file);
    return { ...geojsonToPinsAndLines(gj, service), kind };
  }
  if (kind === "kmz") {
    const gj = await parseKmz(file);
    return { ...geojsonToPinsAndLines(gj, service), kind };
  }
  if (kind === "geojson") {
    const gj = JSON.parse(await file.text());
    return { ...geojsonToPinsAndLines(gj, service), kind };
  }
  if (kind === "gpx") {
    const gj = parseGpxToGeoJson(await file.text());
    return { ...geojsonToPinsAndLines(gj, service), kind };
  }
  if (kind === "csv") {
    return { ...parseCsvToPinsAndLines(await file.text(), service), kind };
  }
  throw new Error("Unsupported file type");
}
